import { ProcedureInstanceBaseParams } from "@/backend/models/procedureInstances/procedureInstanceBase";
import { ContainerInterface, Status } from "./containerTypes";
import { CustomFieldCreateParams, CustomFieldParams } from "./customFieldTypes";
import { OperationInterface } from "./operationTypes";
import { ParameterInterface } from "./parameterTypes";
import { File } from "@/backend/models/file";

export enum ReportType {
  Report = 'report',
  Template = 'template',
  Test = 'test'
}

export interface ReportCreateParams {
  title: string;
  status?: Status;
  reportCreator?: string;
  reportDate?: Date | string;
  requirement?: string;
  testMethod?: string;
  testPlanNumber?: string;
  testPlanRevision?: string;
  type: ReportType;
  reportTemplateId?: string;
  uniqueRemoteId?: string;
  isPreview?: boolean;
  propelOrgId: string;
  projectId?: string;
  /**
   * Templates only. Set: a coded report template — the runs of its procedure
   * template get the coded run report under this key. Unset: a legacy
   * template, a tree of report items generated into a report.
   */
  reportKey?: string | null;
}

/** A coded report template: a report template whose report is a coded run report (see docs/REPORTS.md). */
export function isCodedReportTemplate(report: { type: ReportType; reportKey?: string | null }): report is { type: ReportType.Template; reportKey: string } {
  return report.type === ReportType.Template && typeof report.reportKey === 'string' && report.reportKey.length > 0;
}

export interface ReportParams extends ReportCreateParams {
  id: string;
  createdAt: Date | string;
  updatedAt: Date | string;
}

export enum ReportItemType {
  Text = 'text_item',
  Field = 'field',
  Table = 'table',
  Validation = 'validation',
  Section = 'text',
  Image = 'image',
  Video = 'video'
}


export interface ReportItemBuilderParams {
  type: ReportItemType;
  parentId: string;
  reportId: string;
  position: number;
  textContent?: string;
  headerContent?: string;
  subheader?: string;
  sourceId?: string | null;
  fields?: Omit<CustomFieldCreateParams, 'subjectId' | 'subjectType'>[];
}


export interface ReportItemParams extends ReportItemBuilderParams {
  id: string;
  createdAt?: Date | string;
  updatedAt?: Date | string;
  fields?: CustomFieldParams[];
}

export interface ReportItemInterface extends ReportItemParams {
  textContent: string;
  headerContent: string;
  sourceId: string;
  fields: CustomFieldParams[];
  subheader: string;
}

export interface ReportProcedureObject extends TreeObject<ProcedureInstanceBaseParams> {
  itemsToImport: LeafNode[];
  fullLookup: ContainerItemLookup;
}

export type ContainerItemLookup = { [key: string]: TreeObject<ContainerInterface> };

export type ValidationItem = {
  title: string;
  message: string;
  result: boolean;
};

export type TreeObject<P extends { title: string }> = {
  item: P;
  children: TreeObject<ContainerInterface>[];
  leafNodes: LeafNode[];
  validations?: ValidationItem[];
};

export enum LeafNodeType {
  Operation = 'operation',
  Parameter = 'parameter',
  Validation = 'validation'
}

export interface StepValidationItem {
  id: string;
  title: string;
  sourceId: string;
  validations?: ValidationItem[];
}

export type LeafNode = {
  item: OperationInterface | ParameterInterface | StepValidationItem;
  assets: File[]
  parentId: string;
  fields: CustomFieldParams[];
  type: LeafNodeType;
};

export interface ReportItemTreeObject extends ReportItemInterface {
  reportItems: ReportItemTreeObject[];
  wbs?: string;
}

export type CreateReportProcedureInstance = {
  procedureInstanceId: string;
  importEquipment?: boolean;
  importDut?: boolean;
  status?: Status;
};
